import type { RequestPayload, RequestStatus, RequestType, TranslatableString } from '@geoic/shared';
import { api, type ListResult } from '@/lib/api-client.js';

export type RequestNote = { userId: string; userName?: string; text: string; createdAt: string };

export type RequestRecord = {
  _id: string;
  requestNumber: string;
  type: RequestType;
  status: RequestStatus;
  contact: {
    name: string;
    phone: string;
    email: string | null;
    company: string | null;
    preferredContact: 'phone' | 'telegram' | 'email' | null;
  };
  comment: string | null;
  payload: RequestPayload | null;
  sourceUrl: string;
  locale: string;
  ip: string;
  userAgent: string;
  recaptchaScore: number | null;
  utm: Record<string, string | null> | null;
  assignedTo: string | null;
  notes: RequestNote[];
  estimatedAmount: number | null;
  telegramSentAt: string | null;
  telegramError: string | null;
  createdAt: string;
  updatedAt: string;
};

export type RequestListParams = {
  page?: number;
  limit?: number;
  type?: RequestType;
  status?: RequestStatus;
  assignedTo?: string;
  from?: string;
  to?: string;
  search?: string;
};

export const requestsApi = {
  list: (params: RequestListParams, signal?: AbortSignal) =>
    api.get<ListResult<RequestRecord>>('/requests', { ...params }, signal),

  one: (id: string) => api.get<{ data: RequestRecord }>(`/requests/${id}`).then((r) => r.data),

  update: (
    id: string,
    body: { status?: RequestStatus; assignedTo?: string | null; estimatedAmount?: number | null },
  ) => api.patch<{ data: RequestRecord }>(`/requests/${id}`, body).then((r) => r.data),

  addNote: (id: string, text: string) =>
    api.post<{ data: RequestRecord }>(`/requests/${id}/notes`, { text }).then((r) => r.data),

  exportFile: (params: RequestListParams) => api.download('/requests/export', { ...params }),

  /** Holat yorliqlaridagi sonlar — `{ all, new, in_progress, … }`. */
  counts: () => api.get<Record<string, number>>('/requests/counts'),
};

export const REQUEST_TYPE_LABELS: Record<RequestType, string> = {
  product: 'Mahsulot',
  cart: 'Savatcha',
  drone_show: 'Dron-shou',
  repair: 'Ta’mirlash',
  course: 'Kurs',
  service: 'Xizmat',
  general: 'Umumiy',
};

export const REQUEST_STATUS_LABELS: Record<RequestStatus, string> = {
  new: 'Yangi',
  in_progress: 'Ishda',
  quoted: 'Taklif yuborildi',
  won: 'Yutildi',
  lost: 'Yo‘qotildi',
  spam: 'Spam',
};

/** Uch tilli qiymatdan o'zbekchasini oladi (payload'da ba'zan shunday keladi). */
export function readUz(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && 'uz' in value) {
    return String((value as TranslatableString).uz ?? '');
  }
  return '';
}
